/**
 * @file Showcase 额外预览区块
 * @description 数据展示 / 反馈 / 导航等预览块（拆文件避免页面过大）。
 *
 * @whenToUse 仅由 `src/pages/showcase-page.tsx` 组合引用。
 * @whenNotToUse 业务页面或通用 ui 控件实现。
 */

import { useState } from 'react'
import {
  Bell,
  Columns3,
  CreditCard,
  Ellipsis,
  FolderOpen,
  Home,
  Layers,
  Menu as MenuIcon,
  PanelRight,
  Rows3,
  Settings,
  Tag as TagIcon,
  Users,
} from 'lucide-react'
import alertPrompt from '@/docs/components/alert.md?raw'
import avatarPrompt from '@/docs/components/avatar.md?raw'
import badgePrompt from '@/docs/components/badge.md?raw'
import breadcrumbPrompt from '@/docs/components/breadcrumb.md?raw'
import cardPrompt from '@/docs/components/card.md?raw'
import confirmPrompt from '@/docs/components/confirm.md?raw'
import dividerPrompt from '@/docs/components/divider.md?raw'
import drawerPrompt from '@/docs/components/drawer.md?raw'
import dropdownMenuPrompt from '@/docs/components/dropdown-menu.md?raw'
import emptyPrompt from '@/docs/components/empty.md?raw'
import menuPrompt from '@/docs/components/menu.md?raw'
import paginationPrompt from '@/docs/components/pagination.md?raw'
import popoverPrompt from '@/docs/components/popover.md?raw'
import skeletonPrompt from '@/docs/components/skeleton.md?raw'
import spinPrompt from '@/docs/components/spin.md?raw'
import spinnerPrompt from '@/docs/components/spinner.md?raw'
import tablePrompt from '@/docs/components/table.md?raw'
import tabsPrompt from '@/docs/components/tabs.md?raw'
import tagPrompt from '@/docs/components/tag.md?raw'
import toastPrompt from '@/docs/components/toast.md?raw'
import tooltipPrompt from '@/docs/components/tooltip.md?raw'
import { Alert } from '@/components/ui/alert.tsx'
import { Avatar } from '@/components/ui/avatar.tsx'
import { Badge } from '@/components/ui/badge.tsx'
import { Breadcrumb } from '@/components/ui/breadcrumb.tsx'
import { Button } from '@/components/ui/button.tsx'
import {
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.tsx'
import { confirm } from '@/components/ui/confirm.tsx'
import { Divider } from '@/components/ui/divider.tsx'
import { Drawer } from '@/components/ui/drawer.tsx'
import { DropdownMenu } from '@/components/ui/dropdown-menu.tsx'
import { Empty } from '@/components/ui/empty.tsx'
import { Menu } from '@/components/ui/menu.tsx'
import { Pagination } from '@/components/ui/pagination.tsx'
import { Popover } from '@/components/ui/popover.tsx'
import { ShowcaseCard } from '@/components/ui/showcase-card.tsx'
import { Skeleton } from '@/components/ui/skeleton.tsx'
import { Spin } from '@/components/ui/spin.tsx'
import { Spinner } from '@/components/ui/spinner.tsx'
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  TableSelectionCell,
  TableSelectionHead,
} from '@/components/ui/table.tsx'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx'
import { Tag } from '@/components/ui/tag.tsx'
import { toast } from '@/components/ui/toast.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'

export function ShowcaseExtraSections() {
  return (
    <>
      <ShowcaseCard
        id="card"
        title="Card"
        description="面板容器：Header / Body / Footer。"
        icon={<CreditCard className="size-4" aria-hidden />}
        prompt={cardPrompt}
      >
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>用户概览</CardTitle>
            <Badge variant="accent">Pro</Badge>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-muted">卡片内容区，可放表单或统计信息。</p>
          </CardBody>
          <CardFooter>
            <Button variant="secondary">取消</Button>
            <Button>保存</Button>
          </CardFooter>
        </Card>
      </ShowcaseCard>

      <ShowcaseCard
        id="badge-tag-avatar"
        title="Badge / Tag / Avatar"
        description="状态徽标、可关闭标签、头像。"
        icon={<TagIcon className="size-4" aria-hidden />}
        prompt={`${badgePrompt}\n\n---\n\n${tagPrompt}\n\n---\n\n${avatarPrompt}`}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <Badge>默认</Badge>
            <Badge variant="success">成功</Badge>
            <Badge variant="warning">警告</Badge>
            <Badge variant="danger">危险</Badge>
            <Badge variant="accent">强调</Badge>
          </div>
          <TagDemo />
          <div className="flex items-center gap-3">
            <Avatar fallback="PT" />
            <Avatar alt="Ling" fallback="LS" />
          </div>
        </div>
      </ShowcaseCard>

      <ShowcaseCard
        id="alert-empty-spinner-skeleton"
        title="Alert / Empty / Spinner / Spin / Skeleton"
        description="提示条、空态、加载图标、加载蒙层与骨架。"
        icon={<Bell className="size-4" aria-hidden />}
        prompt={`${alertPrompt}\n\n---\n\n${emptyPrompt}\n\n---\n\n${spinnerPrompt}\n\n---\n\n${spinPrompt}\n\n---\n\n${skeletonPrompt}`}
      >
        <div className="grid gap-4">
          <Alert title="提示" variant="info">
            这是一条信息提示。
          </Alert>
          <Alert title="成功" variant="success">
            操作已完成。
          </Alert>
          <div className="rounded-xl border border-dashed border-border">
            <Empty description="试试调整筛选条件" action={<Button>新建</Button>} />
          </div>
          <div className="flex items-center gap-4">
            <Spinner />
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
          <SpinDemo />
        </div>
      </ShowcaseCard>

      <ShowcaseCard
        id="divider"
        title="Divider"
        description="水平分割线。"
        icon={<Rows3 className="size-4" aria-hidden />}
        prompt={dividerPrompt}
      >
        <div className="max-w-md space-y-3 text-sm">
          <p>上方内容</p>
          <Divider />
          <p>下方内容</p>
        </div>
      </ShowcaseCard>

      <ShowcaseCard
        id="menu"
        title="Menu"
        description="常驻导航：垂直 / 水平、分组 / 子菜单；支持 collapsed 受控收起。"
        icon={<MenuIcon className="size-4" aria-hidden />}
        prompt={menuPrompt}
      >
        <MenuDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="tooltip-dropdown-popover"
        title="Tooltip / DropdownMenu / Popover"
        description="浮层均带 fade，并处理视口边界。"
        icon={<Ellipsis className="size-4" aria-hidden />}
        prompt={`${tooltipPrompt}\n\n---\n\n${dropdownMenuPrompt}\n\n---\n\n${popoverPrompt}`}
      >
        <div className="flex flex-wrap items-center gap-3">
          <Tooltip content="这是一段悬停说明">
            <Button variant="secondary">悬停看 Tooltip</Button>
          </Tooltip>
          <DropdownMenu
            trigger={<Button variant="secondary">更多操作</Button>}
            items={[
              { key: 'edit', label: '编辑', onSelect: () => toast.show('编辑') },
              { key: 'copy', label: '复制', onSelect: () => toast.success('已复制') },
              { key: 'div-1', type: 'divider' },
              {
                key: 'del',
                label: '删除',
                danger: true,
                onSelect: () => toast.danger('已删除'),
              },
            ]}
          />
          <Popover trigger={<Button variant="secondary">打开 Popover</Button>}>
            <p className="mb-2 text-sm font-medium">筛选条件</p>
            <p className="text-xs text-muted">可在此放置简短表单或说明。</p>
          </Popover>
        </div>
      </ShowcaseCard>

      <ShowcaseCard
        id="tabs-breadcrumb"
        title="Tabs / Breadcrumb"
        description="页签与面包屑导航。"
        icon={<Layers className="size-4" aria-hidden />}
        prompt={`${tabsPrompt}\n\n---\n\n${breadcrumbPrompt}`}
      >
        <div className="space-y-4">
          <Breadcrumb
            items={[
              { key: 'home', label: '首页', href: '/' },
              { key: 'users', label: '用户', href: '#' },
              { key: 'detail', label: '详情' },
            ]}
          />
          <TabsDemo />
        </div>
      </ShowcaseCard>

      <ShowcaseCard
        id="table-pagination"
        title="Table / Pagination"
        description="基础表格、首列多选、行 hover、固定列与分页。"
        icon={<Columns3 className="size-4" aria-hidden />}
        prompt={`${tablePrompt}\n\n---\n\n${paginationPrompt}`}
      >
        <TablePaginationDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="drawer"
        title="Drawer"
        description="右侧抽屉；位移 + 遮罩 fade。"
        icon={<PanelRight className="size-4" aria-hidden />}
        prompt={drawerPrompt}
      >
        <DrawerDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="toast-confirm"
        title="Toast / Confirm"
        description="全局轻提示与命令式确认框。"
        icon={<Bell className="size-4" aria-hidden />}
        prompt={`${toastPrompt}\n\n---\n\n${confirmPrompt}`}
      >
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => toast.success('保存成功', '配置已更新')}
          >
            Toast 成功
          </Button>
          <Button
            variant="secondary"
            onClick={() => toast.danger('操作失败', '请稍后重试')}
          >
            Toast 失败
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              const ok = await confirm({
                title: '确认删除？',
                description: '删除后不可恢复。',
                danger: true,
                confirmText: '删除',
              })
              if (ok) toast.success('已删除')
            }}
          >
            Confirm 删除
          </Button>
        </div>
      </ShowcaseCard>
    </>
  )
}

function SpinDemo() {
  const [spinning, setSpinning] = useState(true)
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted">Spin 蒙层（backdrop-blur 4px）</p>
        <Button variant="secondary" onClick={() => setSpinning((v) => !v)}>
          {spinning ? '停止' : '加载'}
        </Button>
      </div>
      <Spin
        spinning={spinning}
        tip="加载中…"
        className="rounded-xl border border-border bg-raised"
      >
        <div className="min-h-36 p-4">
          <p className="text-sm font-medium text-foreground">卡片内容区</p>
          <p className="mt-2 text-sm text-muted">
            spinning 时内容半透明，蒙层随容器圆角裁切（overflow +
            rounded inherit）。
          </p>
        </div>
      </Spin>
    </div>
  )
}

function MenuDemo() {
  const [selected, setSelected] = useState(['overview'])
  const [collapsed, setCollapsed] = useState(false)
  const menuItems = [
    {
      key: 'overview',
      label: '概览',
      icon: <Home aria-hidden />,
    },
    {
      key: 'users-group',
      label: '用户',
      type: 'group' as const,
      children: [
        {
          key: 'users',
          label: '用户列表',
          icon: <Users aria-hidden />,
        },
        {
          key: 'roles',
          label: '角色权限',
          icon: <Settings aria-hidden />,
        },
      ],
    },
    {
      key: 'reports',
      label: '报表',
      icon: <FolderOpen aria-hidden />,
      children: [
        { key: 'daily', label: '日报' },
        { key: 'weekly', label: '周报' },
      ],
    },
  ]
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-border bg-raised p-3">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-xs text-muted">垂直 + 受控收起</p>
          <Button
            variant="secondary"
            onClick={() => setCollapsed((v) => !v)}
          >
            {collapsed ? '展开' : '收起'}
          </Button>
        </div>
        <Menu
          mode="vertical"
          collapsed={collapsed}
          onCollapsedChange={setCollapsed}
          selectedKeys={selected}
          defaultOpenKeys={['reports']}
          onSelect={(key) => setSelected([key])}
          items={menuItems}
        />
      </div>
      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-raised p-3">
          <p className="mb-2 text-xs text-muted">水平菜单</p>
          <Menu
            mode="horizontal"
            selectedKeys={selected}
            onSelect={(key) => setSelected([key])}
            items={[
              { key: 'overview', label: '概览', icon: <Home aria-hidden /> },
              { key: 'users', label: '用户', icon: <Users aria-hidden /> },
              { key: 'settings', label: '设置', icon: <Settings aria-hidden /> },
            ]}
          />
          <p className="mt-3 text-xs text-muted">当前选中：{selected[0]}</p>
        </div>
        <div className="rounded-xl border border-border bg-raised p-3">
          <p className="mb-2 text-xs text-muted">内置 collapsible 按钮</p>
          <Menu
            mode="vertical"
            collapsible
            defaultCollapsed={false}
            selectedKeys={selected}
            onSelect={(key) => setSelected([key])}
            items={menuItems}
          />
        </div>
      </div>
    </div>
  )
}

function TagDemo() {
  const [tags, setTags] = useState(['React', 'Vite', 'Tailwind'])
  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((t) => (
        <Tag key={t} onClose={() => setTags((all) => all.filter((x) => x !== t))}>
          {t}
        </Tag>
      ))}
    </div>
  )
}

function TabsDemo() {
  const [tab, setTab] = useState('a')
  return (
    <Tabs value={tab} onChange={setTab}>
      <TabsList>
        <TabsTrigger value="a">基本信息</TabsTrigger>
        <TabsTrigger value="b">权限</TabsTrigger>
        <TabsTrigger value="c">日志</TabsTrigger>
      </TabsList>
      <TabsContent value="a">基本信息内容</TabsContent>
      <TabsContent value="b">权限配置内容</TabsContent>
      <TabsContent value="c">操作日志内容</TabsContent>
    </Tabs>
  )
}

function TablePaginationDemo() {
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<string[]>([])
  const rows = [
    {
      id: '1',
      name: 'Alice',
      role: 'Admin',
      dept: '工程',
      city: '上海',
      email: 'alice@example.com',
      phone: '138****0001',
    },
    {
      id: '2',
      name: 'Bob',
      role: 'Editor',
      dept: '设计',
      city: '北京',
      email: 'bob@example.com',
      phone: '138****0002',
    },
    {
      id: '3',
      name: 'Carol',
      role: 'Viewer',
      dept: '运营',
      city: '深圳',
      email: 'carol@example.com',
      phone: '138****0003',
    },
  ]
  const allIds = rows.map((row) => row.id)
  const allSelected =
    allIds.length > 0 && allIds.every((id) => selected.includes(id))
  const someSelected = selected.length > 0 && !allSelected

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted">
        横向滚动查看：左侧多列固定（选择 + 姓名）、右侧操作列固定；行 hover 时固定列同步变色。
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableSelectionHead
              fixed="left"
              stickyOffset={0}
              width={40}
              checked={allSelected}
              indeterminate={someSelected}
              onCheckedChange={(checked) => {
                setSelected(checked ? allIds : [])
              }}
            />
            <TableHead
              fixed="left"
              stickyOffset={40}
              fixedEdge
              width={100}
            >
              姓名
            </TableHead>
            <TableHead width={100}>角色</TableHead>
            <TableHead width={100}>部门</TableHead>
            <TableHead width={100}>城市</TableHead>
            <TableHead width={180}>邮箱</TableHead>
            <TableHead width={140}>手机</TableHead>
            <TableHead fixed="right" stickyOffset={0} fixedEdge width={88}>
              操作
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableEmpty colSpan={8} />
          ) : (
            rows.map((row) => (
              <TableRow key={row.id}>
                <TableSelectionCell
                  fixed="left"
                  stickyOffset={0}
                  width={40}
                  checked={selected.includes(row.id)}
                  onCheckedChange={(checked) => {
                    setSelected((prev) =>
                      checked
                        ? [...prev, row.id]
                        : prev.filter((id) => id !== row.id),
                    )
                  }}
                  aria-label={`选择 ${row.name}`}
                />
                <TableCell
                  fixed="left"
                  stickyOffset={40}
                  fixedEdge
                  width={100}
                >
                  {row.name}
                </TableCell>
                <TableCell width={100}>{row.role}</TableCell>
                <TableCell width={100}>{row.dept}</TableCell>
                <TableCell width={100}>{row.city}</TableCell>
                <TableCell width={180}>{row.email}</TableCell>
                <TableCell width={140}>{row.phone}</TableCell>
                <TableCell fixed="right" stickyOffset={0} fixedEdge width={88}>
                  <Button variant="ghost">编辑</Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <p className="text-xs text-muted">
        已选 {selected.length} 行
        {selected.length > 0 ? `：${selected.join(', ')}` : ''}
      </p>
      <Pagination page={page} pageSize={10} total={37} onChange={setPage} />
    </div>
  )
}

function DrawerDemo() {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Button onClick={() => setOpen(true)}>打开 Drawer</Button>
      <Drawer
        open={open}
        onOpenChange={setOpen}
        title="编辑用户"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              取消
            </Button>
            <Button onClick={() => setOpen(false)}>保存</Button>
          </>
        }
      >
        <p className="text-sm text-muted">抽屉内容区，长内容可滚动。</p>
        {Array.from({ length: 10 }, (_, i) => (
          <p key={i} className="mt-2 text-sm">
            字段示例 {i + 1}
          </p>
        ))}
      </Drawer>
    </>
  )
}
